import {
  EnvironmentProviders,
  Provider,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { TranslateService, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom } from 'rxjs';

export type VhLocale = 'en' | 'hi' | 'gu';
const SUPPORTED: VhLocale[] = ['en', 'hi', 'gu'];
const STORAGE_KEY = 'vh-lang';

/**
 * Registers ngx-translate with the app, loads the initial locale from
 * localStorage (falling back to `en`) and boots before the first route.
 *
 * SSR-safe: localStorage access is guarded via typeof checks.
 */
export function provideTranslation(): EnvironmentProviders {
  const providers: Provider[] = [
    ...provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
    }),
  ];

  return makeEnvironmentProviders([
    ...providers,
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      translate.addLangs(SUPPORTED);
      translate.setFallbackLang('en');
      const lang = resolveInitialLocale();
      return firstValueFrom(translate.use(lang));
    }),
  ]);
}

function resolveInitialLocale(): VhLocale {
  try {
    if (typeof localStorage === 'undefined') return 'en';
    const stored = localStorage.getItem(STORAGE_KEY) as VhLocale | null;
    if (stored && (SUPPORTED as string[]).includes(stored)) return stored;
  } catch {
    /* SSR / private mode — fall through */
  }
  return 'en';
}
